/**
 * Signup, step 1 — the whole of the script on `https://lifekees.com/signup/`.
 *
 * Deliberately small and deliberately *not* the app: no Svelte, no router, no
 * vault, no crypto worker, no API client. This document runs Cloudflare's
 * `api.js` in its own realm, so what it can reach is bounded by what this
 * bundle contains, and the answer is meant to be "an email field, a widget and
 * one `fetch`". `test/signup-isolation.test.ts` holds the module graph to that.
 *
 * The flow:
 *
 *  0. wait for the old app's storage on this origin to be wiped
 *     (`old-origin/wipe.ts`); `loadThirdPartyScript` will not load Cloudflare's
 *     script before that, and if the wipe cannot complete (an old-app tab is
 *     holding the database) this page shows a notice and never loads it;
 *  1. load and render the Turnstile widget (Managed mode — Cloudflare decides
 *     whether a click is needed) with `action: 'signup'`, which the server
 *     checks;
 *  2. on Continue, POST `{email, turnstileToken}` to the ticket endpoint (the
 *     one API route nginx proxies on this origin);
 *  3. on success, a **full navigation** to the vault's registration screen,
 *     `https://vault.lifekees.com/app/register`, with the ticket in the URL
 *     fragment. That navigation is the security boundary: it crosses to the
 *     vault's origin and throws this document — and Cloudflare's script with
 *     it — away before any secret exists.
 *
 * Turnstile tokens are single-use and live 300 seconds, so every failure path
 * resets the widget for a fresh one rather than resubmitting a spent token.
 */
// FIRST, on purpose: starts the old-origin wipe before anything else on this
// page evaluates. `loadThirdPartyScript` below waits for it regardless, so this
// is about starting early, not about correctness.
import './old-origin/wipe.js';
import '../../src/app.css';
import './signup.css';
import {
  APP_ORIGIN,
  SIGNUP_TICKET_ENDPOINT,
  TURNSTILE_SITEKEY,
  handoffUrl,
} from '../../src/lib/signup-ticket.js';
import { OldOriginNotWipedError, loadThirdPartyScript } from './third-party.js';

/** The slice of Cloudflare's global this page uses. */
interface TurnstileApi {
  render(container: string | HTMLElement, options: Record<string, unknown>): string | undefined;
  reset(widgetId?: string): void;
}

/** Same shape the server's `Email` schema enforces; the server re-checks. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function byId<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`#${id} is missing from signup/index.html`);
  return element as T;
}

const form = byId<HTMLFormElement>('signup-form');
const emailInput = byId<HTMLInputElement>('signup-email');
const submit = byId<HTMLButtonElement>('signup-continue');
const status = byId<HTMLParagraphElement>('signup-status');
const errorBox = byId<HTMLDivElement>('signup-error');
// Not `id="turnstile"`: an element id is a named property of `window`, so a
// container called that would *be* `window.turnstile` whenever Cloudflare's
// script failed to load, and the check below would mistake it for the API.
const widgetHost = byId<HTMLDivElement>('turnstile-widget');

byId<HTMLAnchorElement>('signin-link').href = `${APP_ORIGIN}/app`;

let token: string | null = null;
let widgetId: string | undefined;
let busy = false;

function update(): void {
  submit.disabled = busy || token === null || !EMAIL_PATTERN.test(emailInput.value.trim());
  submit.textContent = busy ? 'Checking…' : 'Continue';
}

function say(message: string): void {
  status.textContent = message;
}

function fail(message: string | null): void {
  errorBox.textContent = message ?? '';
  errorBox.hidden = message === null;
}

/** Cloudflare's global, if `api.js` actually loaded and ran. */
function turnstileApi(): TurnstileApi | null {
  const candidate = (globalThis as { turnstile?: Partial<TurnstileApi> }).turnstile;
  return candidate && typeof candidate.render === 'function' && typeof candidate.reset === 'function'
    ? (candidate as TurnstileApi)
    : null;
}

/** A spent or refused token is no use again: ask Cloudflare for a new one. */
function freshChallenge(): void {
  token = null;
  update();
  const turnstile = turnstileApi();
  if (turnstile && widgetId !== undefined) turnstile.reset(widgetId);
}

function renderWidget(): void {
  const turnstile = turnstileApi();
  if (!turnstile) {
    // A content blocker, a network that cannot reach Cloudflare, or a CSP that
    // refused it. The page is useless without it, so say so plainly.
    say('');
    fail(
      'The check that keeps bots out could not load. If you use a content blocker, allow ' +
        'challenges.cloudflare.com on this page, or try another network — then reload.',
    );
    return;
  }

  widgetId = turnstile.render(widgetHost, {
    sitekey: TURNSTILE_SITEKEY,
    action: 'signup',
    theme: 'auto',
    appearance: 'always',
    // The normal widget is a fixed 300px; at 320px (iPhone SE) the column is
    // ~274px after the gutters. Compact (150px) fits every phone width.
    size: widgetHost.clientWidth >= 300 ? 'normal' : 'compact',
    // We read the token from the callback; no hidden form field is needed,
    // and none should be submitted anywhere by accident.
    'response-field': false,
    callback: (value: string) => {
      token = value;
      fail(null);
      say('Done — you can continue.');
      update();
    },
    // Tokens last five minutes. Turnstile refreshes an expired one itself
    // (`refresh-expired: auto`); until it has, there is nothing to submit.
    'expired-callback': () => {
      token = null;
      say('That check expired. Refreshing it…');
      update();
    },
    // Managed mode's interactive challenge: Cloudflare wants one click.
    'before-interactive-callback': () => {
      say('One click to confirm you are a person.');
    },
    'after-interactive-callback': () => {
      say('Checking…');
    },
    // The visitor left the interactive challenge unanswered too long.
    'timeout-callback': () => {
      say('The check timed out. Try it again.');
      freshChallenge();
    },
    'error-callback': (code: string) => {
      token = null;
      update();
      say('');
      fail(
        `The check that keeps bots out did not work (${code}). Reload the page to try again; ` +
          'if it keeps happening, try another browser or network.',
      );
      // Handled: stops Turnstile from also throwing to the console.
      return true;
    },
    'unsupported-callback': () => {
      fail('This browser cannot run the check that keeps bots out. Try a current browser.');
    },
  });
}

async function requestTicket(email: string, turnstileToken: string): Promise<void> {
  let response: Response;
  try {
    response = await fetch(SIGNUP_TICKET_ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, turnstileToken }),
      // No cookies exist to send, and none should be accepted.
      credentials: 'omit',
    });
  } catch {
    fail('Lifekees could not be reached. Check your connection and try again.');
    freshChallenge();
    return;
  }

  let body: { ticket?: unknown; error?: unknown; message?: unknown } = {};
  try {
    body = (await response.json()) as typeof body;
  } catch {
    /* handled by status below */
  }

  if (response.ok && typeof body.ticket === 'string') {
    say('Taking you to the next step…');
    // Replace, not push: Back from step 2 should not land on a page holding a
    // spent token and a used ticket.
    location.replace(handoffUrl(body.ticket));
    return;
  }

  if (response.status === 429) {
    fail('Too many attempts from this network. Wait a few minutes and try again.');
  } else if (response.status === 400) {
    fail('That does not look like an email address.');
  } else if (typeof body.message === 'string') {
    fail(body.message);
  } else {
    fail('Something went wrong on our side. Try again in a minute.');
  }
  freshChallenge();
}

form.addEventListener('submit', (event) => {
  event.preventDefault();
  const email = emailInput.value.trim();
  if (busy || token === null) return;
  if (!EMAIL_PATTERN.test(email)) {
    fail('That does not look like an email address.');
    return;
  }
  fail(null);
  busy = true;
  update();
  void requestTicket(email, token).finally(() => {
    busy = false;
    update();
  });
});

emailInput.addEventListener('input', update);

update();
emailInput.focus();

// Cloudflare's script is loaded here, by the one loader allowed to, only after
// the old app's storage on this origin is gone. Its `load` event is the moment
// its global exists; an error (a content blocker, a network that cannot reach
// Cloudflare, a CSP refusal) leaves `turnstileApi()` null, which
// `renderWidget` reports.
loadThirdPartyScript('turnstile').then(renderWidget, (error: unknown) => {
  if (error instanceof OldOriginNotWipedError) {
    // Fail closed. This is never the "old tab is open" case: a delete blocked
    // by an open tab does not produce a report at all — the wipe keeps waiting
    // and `#old-origin-notice` asks the user to close that tab. A report that
    // arrives incomplete means a step FAILED: the browser refused or errored
    // on deleting something (a corrupted profile, an IndexedDB that errors in
    // this mode). Reloading will not change that, and telling a first-time
    // visitor to close an old Lifekees tab they never had would be a dead end
    // with a wrong explanation.
    say('');
    fail(
      'This browser would not let Lifekees clear out data left by the old version of the app, ' +
        'so the check that keeps bots out cannot load here. Try a normal (not private) window, or ' +
        'another browser.',
    );
    return;
  }
  renderWidget();
});
